// Journey math: each user gets their own 27-week (189-day) challenge that
// starts the first time they load the page. Persisted via localStorage so
// the same learner sees a consistent trajectory across sessions. Works when
// embedded in an LMS as long as the iframe permits storage.
//
// Overrides supported (in priority order):
//   1. ?start=YYYY-MM-DD in the URL (useful for testing / instructor previews)
//   2. localStorage['icelandic_ai.journey_start']
//   3. Today's date (set on first visit).
const STORAGE_KEY = "icelandic_ai.journey_start";
const TOTAL_DAYS = 26 * 7; // 182
const MS_PER_DAY = 86400000;

function parseISODate(str) {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function safeLocalStorage() {
  try {
    if (typeof window === "undefined") return null;
    const t = "__ls_test__";
    window.localStorage.setItem(t, "1");
    window.localStorage.removeItem(t);
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Resolve (and persist) the learner's personal start date.
 * Returns a Date at 00:00 local time.
 */
export function getJourneyStart(now = new Date()) {
  // 1. URL override — instructor-friendly for demos.
  if (typeof window !== "undefined" && window.location) {
    const params = new URLSearchParams(window.location.search);
    const override = parseISODate(params.get("start"));
    if (override) return override;
  }

  const ls = safeLocalStorage();
  if (ls) {
    const stored = parseISODate(ls.getItem(STORAGE_KEY));
    if (stored) return stored;
  }

  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (ls) {
    ls.setItem(STORAGE_KEY, toISODate(start));
  }
  return start;
}

/**
 * Compute the learner's progress along a 189-day training journey that starts
 * on their personal `start` date. `end` = start + 189 days.
 */
export function computeJourney(now = new Date(), startOverride = null) {
  const start = startOverride || getJourneyStart(now);
  const end = new Date(start);
  end.setDate(end.getDate() + TOTAL_DAYS);

  const rawElapsed = Math.floor((now - start) / MS_PER_DAY);
  const elapsedDays = Math.max(0, Math.min(TOTAL_DAYS, rawElapsed));
  const progress = elapsedDays / TOTAL_DAYS;
  const currentWeek = Math.min(26, Math.max(1, Math.floor(elapsedDays / 7) + 1));
  const daysRemaining = Math.max(0, Math.ceil((end - now) / MS_PER_DAY));

  return {
    start,
    end,
    elapsedDays,
    totalDays: TOTAL_DAYS,
    progress,
    currentWeek,
    daysRemaining,
  };
}

export function beltForWeek(belts, week) {
  return (
    belts.find(
      (b) =>
        !b.locked &&
        b.weekStart != null &&
        week >= b.weekStart &&
        week <= b.weekEnd
    ) || belts[0]
  );
}

export function formatLongDate(d) {
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatShortDate(d) {
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
